// MakeARequest.jsx
import React, { useEffect, useState } from "react";
import SelectBox from "./controls/SelectBox";
import Axios from "axios";
import * as constants from "../constants";
import TextBox from "./controls/TextBox";
import DateBox from "./controls/DateBox";
import AutoComplete from "./common/AutoComplete";
import Alert from "./common/Alert";
import { ERRORICON, SUCCESSICON } from "../IconConstants";
import ErrorSound from "../assets/sounds/error.mp3";
import SubmitSound from "../assets/sounds/submit.mp3";
import FormLabel from "./controls/FormLabel";
import moment from "moment";
import Uploader from "./controls/Uploader";
import { toast } from "react-toastify";
import { 
  FileText, 
  Download, 
  Send, 
  Clock, 
  CheckCircle2, 
  XCircle, 
  AlertCircle,
  FileUp,
  History,
  ChevronRight,
  Loader2,
  ClipboardList,
  GraduationCap,
  Calendar,
  DollarSign,
  MessageSquare,
  Info
} from "lucide-react";

// Loading Skeleton
const MakeARequestSkeleton = () => {
  return (
    <div className="animate-pulse space-y-6">
      <div className="h-8 bg-gray-200 rounded-lg w-1/3"></div>
      <div className="h-12 bg-gray-200 rounded-xl w-full"></div>
      <div className="h-12 bg-gray-200 rounded-xl w-full"></div>
      <div className="flex gap-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-10 bg-gray-200 rounded-full w-32"></div>
        ))}
      </div>
    </div>
  );
};

// Status Badge Component
const StatusBadge = ({ status }) => {
  const statusConfig = {
    processed: {
      bg: "bg-green-50",
      text: "text-green-700",
      border: "border-green-200",
      icon: CheckCircle2,
      label: "Approved"
    },
    pending: {
      bg: "bg-amber-50",
      text: "text-amber-700",
      border: "border-amber-200",
      icon: Clock,
      label: "Pending"
    },
    declined: {
      bg: "bg-red-50",
      text: "text-red-700",
      border: "border-red-200",
      icon: XCircle,
      label: "Declined"
    }
  };

  const config = statusConfig[status.toLowerCase()] || statusConfig.pending;
  const Icon = config.icon;

  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${config.bg} ${config.text} ${config.border}`}>
      <Icon className="w-3 h-3" />
      {config.label}
    </span>
  );
};

// Empty State Component
const EmptyState = ({ icon: Icon, title, description }) => {
  return (
    <div className="flex flex-col items-center justify-center py-8 px-4">
      <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
        <Icon className="w-6 h-6 text-gray-400" />
      </div>
      <h4 className="text-sm font-medium text-gray-900 mb-1">{title}</h4>
      <p className="text-xs text-gray-500 text-center">{description}</p>
    </div>
  );
};

// Section Header Component
const SectionHeader = ({ icon: Icon, title, subtitle, color = "blue" }) => {
  const colorClasses = {
    blue: "bg-blue-100 text-blue-600",
    purple: "bg-purple-100 text-purple-600",
    amber: "bg-amber-100 text-amber-600",
    green: "bg-green-100 text-green-600"
  };

  return (
    <div className="flex items-center gap-3 mb-4">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${colorClasses[color]}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
      </div>
    </div>
  );
};

const MakeARequest = () => {
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [programSelected, setProgramSelected] = useState(false);
  const [requestSelected, setRequestSelected] = useState("");
  const [refresh, setRefresh] = useState(false);
  const [avatarurl, setAvatarurl] = useState(localStorage.getItem("avatarurl"));
  const [studentCourses, setStudentCourses] = useState([]);
  const [studentRequests, setStudentRequests] = useState([]);
  const [currentRequests, setCurrentRequests] = useState([]);
  const [pastRequests, setPastRequests] = useState([]);
  const [changeCoursesData, setChangeCoursesData] = useState([]);
  const [campus, setCampus] = useState("");
  const [programDisable, setProgramDisable] = useState(false);
  const [notesBox, setNotesBox] = useState("hidden");
  const [btnHidden, setBtnHidden] = useState("hidden");
  const [amountBox, setAmountBox] = useState("hidden");
  const [dateBoxes, setDateBoxes] = useState("hidden");
  const [newPHidden, setNewPHidden] = useState("hidden");
  const [paidAmount, setPaidAmount] = useState(0);
  const [errorAudio] = useState(new Audio(ErrorSound));
  const [submitAudio] = useState(new Audio(SubmitSound));
  const studentId = localStorage.getItem("studentId");

  const [showDocs, setShowDocs] = useState(false);
  const [docs, setDocs] = useState([]);
  const [pendingRequestFound, setPendingRequestFound] = useState(false);
  const [uploadedDocs, setUploadDocs] = useState([]);
  const [history, setHistory] = useState([]);
  const [tempHistory, setTempHistory] = useState([]);
  const [isUploadedFileValidError, setIsUploadedFileValidError] = useState(null);

  const [requestData, setRequestData] = useState({
    enrolmentId: "",
    requestValue: "",
    notes: "",
    courseId: "",
    amount: "",
    startDate: "",
    endDate: "",
  });
  
  const [errors, setErrors] = useState({
    amount: "",
    startDate: "",
    endDate: "",
    newCourse: "",
    notes: "",
  });
  
  const [alertState, setAlertState] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertIcon, setAlertIcon] = useState("");
  const [iconColor, setIconColor] = useState("");

  const playSound = (audioFile) => {
    audioFile.play();
  };

  const studentProgramsGet = () => {
    Axios.get(constants.BASEURL + "Student/0/Programs/" + studentId)
      .then((response) => {
        let currentPrograms = response?.data;
        setStudentCourses(currentPrograms);
      })
      .catch(function (error) {
        console.log(error);
      })
      .finally(() => {
        setPageLoading(false);
      });
  };

  const allRequestsGet = () => {
    Axios.get(constants.BASEURL + "StudentRequests")
      .then((response) => {
        let requestsData = response.data;
        let pastR = requestsData.filter(function (item) {
          return item.value == 4;
        });
        setPastRequests(pastR);
        setCurrentRequests(requestsData);
      })
      .catch(function (error) {
        console.log(error);
      });
  };

  const handleAutoControl = (e, name) => {
    let controlName = "";
    let value = "";
    let rData = { ...requestData };
    let errorD = errors;
    if (name) {
      controlName = e;
      value = name.programCampusID;
    }

    if (controlName === "NewProgram" && value) {
      errorD.newCourse = "";
      rData.startDate = "";
      rData.endDate = "";
      rData.courseId = value;
      setRequestData(rData);
      setErrors(errorD);
    }
  };

  const checkForPendingRequests = (historyData) => {
    setPendingRequestFound(historyData.some((item) => item.requestStatus.toLowerCase() === "pending"));
  };

  const getDocumentHistory = async (enrolmentId) => {
    try {
      const res = await Axios.get(constants.BASEURL + `StudentRequests/RequestHistory/${enrolmentId}`);
      setHistory(res.data);
      setTempHistory(res.data);
    } catch (error) {
      console.log(error);
    }
  };

  const handleChange = (e, name, request) => {
    let controlName = "";
    let value = "";
    let rData = { ...requestData };
    let errorD = errors;
    
    if (name) {
      controlName = name;
      value = e;
    } else {
      controlName = e.currentTarget.name;
      value = e.currentTarget.value;
    }
    
    if (controlName === "Program") {
      let selectedProgram = studentCourses.filter(function (item) {
        return item.enrolmentId === +value;
      });

      if (selectedProgram.length > 0) {
        setProgramSelected(true);
      } else {
        setProgramSelected(false);
      }

      let programType = selectedProgram[0]?.currentProgram;
      let paidAmnt = selectedProgram[0]?.totalReceipted;
      let campusId = selectedProgram[0]?.campusId;

      getDocumentHistory(selectedProgram[0]?.enrolmentId);

      if (programType === 1) {
        if (selectedProgram[0]?.totalReceipted <= 0) {
          setStudentRequests(currentRequests.filter((request) => request.value !== 2));
        } else {
          setStudentRequests(currentRequests);
        }
      } else if (programType === 0) {
        setStudentRequests(pastRequests);
      }

      rData.enrolmentId = value ? parseInt(value) : "";
      rData.startDate = "";
      rData.endDate = "";
      rData.courseId = "";
      rData.notes = "";
      rData.requestValue = "";
      rData.amount = "";
      setRequestData(rData);
      setPaidAmount(paidAmnt);
      setCampus(parseInt(campusId));

      setNotesBox("hidden");
      setBtnHidden("hidden");
      setAmountBox("hidden");
      setDateBoxes("hidden");
      setNewPHidden("hidden");
      setShowDocs(false);
      setHistory([]);
      setRequestSelected("");
      setUploadDocs([]);
    } else if (controlName === "Request") {
      setShowDocs(true);
      setRequestSelected(request.codeName);
      setUploadDocs([]);
      setDocs(request.documents);

      let filteredRequests = history.filter((item) => item.requestType === request.userCodeId);
      setTempHistory(filteredRequests);
      checkForPendingRequests(filteredRequests);

      rData.startDate = "";
      rData.endDate = "";
      rData.courseId = "";
      rData.notes = "";
      rData.amount = "";
      rData.requestValue = request.userCodeId;
      setRequestData(rData);
    } else if (controlName === "notes") {
      errorD.notes = "";
      rData.notes = value;
      setRequestData(rData);
      setErrors(errorD);
    }
  };

  const closeAlert = () => {
    setAlertState(false);
  };

  useEffect(() => {
    if (alertState && iconColor === "green") {
      setRefresh(!refresh);
    }
  }, [alertState && iconColor]);

  const requestsDataSavePost = async (pData) => {
    let isFileValid = uploadedDocs.some((file) => file.type !== "application/pdf");

    if (isFileValid) {
      playSound(errorAudio);
      return toast.error('Only PDF format files are acceptable');
    }

    let docNameArr = [];
    uploadedDocs.forEach((doc) => docNameArr.push(doc.name));
    let uniqueSet = new Set(docNameArr);

    if (docNameArr.length !== uniqueSet.size) {
      playSound(errorAudio);
      return toast.error('You cannot upload duplicate documents');
    }

    setLoading(true);
    const toastId = "submitrequesttoast";

    try {
      toast("Submitting Request...", {
        toastId,
        type: "info",
        autoClose: false
      });

      const submitRequest = await Axios.post(constants.BASEURL + "StudentRequests", pData);

      if (submitRequest.data.toString().startsWith("An error")) {
        throw "Unable to submit request";
      }

      let formData = new FormData();
      uploadedDocs.forEach((doc) => {
        formData.append("file", doc);
      });

      formData.append("objectId", pData.enrolmentId);
      formData.append("studentRequestId", submitRequest.data);

      await Axios.post(constants.BASEURL + "StudentRequests/SaveRequestForms", formData);

      toast.update(toastId, {
        render: 'Request Sent Successfully',
        type: "success",
        autoClose: 3000
      });

      playSound(submitAudio);
    } catch (error) {
      playSound(errorAudio);
      toast.update(toastId, {
        render: 'An error occurred while submitting request',
        type: "error",
        autoClose: 3000
      });
      console.log(error);
    } finally {
      setRefresh(!refresh);
      setUploadDocs([]);
      setLoading(false);
    }
  };

  const submitClicked = () => {
    let requestD = requestData;
    let postData = {
      enrolmentId: requestD.enrolmentId == "" ? null : parseInt(requestD.enrolmentId),
      amount: requestD.amount == "" ? null : parseInt(requestD.amount),
      startDate: requestD.startDate == "" ? null : requestD.startDate,
      endDate: requestD.endDate == "" ? null : requestD.endDate,
      courseId: requestD.courseId == "" ? null : parseInt(requestD.courseId),
      notes: requestD.notes == "" ? null : requestD.notes,
      requestType: requestD.requestValue == "" ? null : parseInt(requestD.requestValue),
    };

    requestsDataSavePost(postData);
  };

  const resetData = () => {
    setProgramSelected(false);
    setRequestSelected("");
    setStudentCourses([]);
    setStudentRequests([]);
    setCurrentRequests([]);
    setPastRequests([]);
    setChangeCoursesData([]);
    setCampus("");
    setShowDocs(false);
    setHistory([]);
    setTempHistory([]);
    setNotesBox("hidden");
    setBtnHidden("hidden");
    setAmountBox("hidden");
    setDateBoxes("hidden");
    setNewPHidden("hidden");
    setPaidAmount(0);
    setRequestData({
      enrolmentId: "",
      requestValue: "",
      notes: "",
      courseId: "",
      amount: "",
      startDate: "",
      endDate: "",
    });
    setErrors({
      amount: "",
      startDate: "",
      endDate: "",
      newCourse: "",
      notes: "",
    });
  };

  useEffect(() => {
    resetData();
    studentProgramsGet();
    allRequestsGet();
  }, [refresh]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 overflow-x-hidden">
      <div className="py-8 px-4 sm:px-0">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-blue-100">
              <ClipboardList className="w-6 h-6 text-blue-500" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Make a Request</h1>
              <p className="text-gray-500 text-sm">Submit your requests and track their status</p>
            </div>
          </div>
        </div>

        {pageLoading ? (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8">
            <MakeARequestSkeleton />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Program Selection Card */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8">
              <SectionHeader 
                icon={GraduationCap} 
                title="Select Program" 
                subtitle="Choose the program for your request"
                color="blue"
              />
              
              <div className="mt-4 w-full max-w-full min-w-0 overflow-hidden">
                <SelectBox
                  dependentOn={true}
                  key={"Program"}
                  id={"Program"}
                  name={"Program"}
                  disabled={programDisable}
                  label=""
                  data={studentCourses}
                  onChange={handleChange}
                  multiple={0}
                  idField={"enrolmentId"}
                  valueField="courseName"
                  value={requestData?.enrolmentId || ""}
                  fullwidth={true}
                  placeholder="Select a program..."
                  className="w-full rounded-xl transition-all duration-200"
                />
              </div>
            </div>

            {/* Request Type Selection */}
            {programSelected && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8 animate-fadeIn">
                <SectionHeader 
                  icon={FileText} 
                  title="Request Type" 
                  subtitle="What would you like to request?"
                  color="blue"
                />
                
                <div className="flex flex-wrap gap-3 mt-4">
                  {studentRequests?.map((request, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleChange(request.value, "Request", request)}
                      className={`px-5 py-2.5 rounded-xl font-medium text-sm transition-all duration-200 flex items-center gap-2
                        ${requestSelected === request.codeName
                          ? "bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg shadow-blue-500/25 scale-105"
                          : "bg-gray-100 text-gray-700 hover:bg-gray-200 hover:scale-102"
                        }`}
                    >
                      <ChevronRight className={`w-4 h-4 transition-transform duration-200 ${requestSelected === request.codeName ? "rotate-90" : ""}`} />
                      {request.codeName}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* New Program Selection */}
            {newPHidden !== "hidden" && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8 animate-fadeIn">
                <SectionHeader 
                  icon={GraduationCap} 
                  title="New Program" 
                  subtitle="Select your new program"
                  color="green"
                />
                
                <div className="mt-4">
                  <AutoComplete
                    name={"NewProgram"}
                    error={errors?.newCourse}
                    endPoint={"Campus/Programs?campusId=" + campus}
                    dependentOn={"no"}
                    label={""}
                    onChange={handleAutoControl}
                  />
                </div>
              </div>
            )}

            {/* Refund Amount */}
            {amountBox !== "hidden" && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8 animate-fadeIn">
                <SectionHeader 
                  icon={DollarSign} 
                  title="Refund Amount" 
                  subtitle="Enter the amount you'd like to request"
                  color="green"
                />
                
                {paidAmount > 0 ? (
                  <div className="mt-4">
                    <TextBox
                      key={"rAmount"}
                      id={"rAmount"}
                      label=""
                      readOnly={false}
                      error={errors?.amount}
                      value={requestData?.amount}
                      name="refundAmount"
                      type="number"
                      dependentOn={"no"}
                      onChange={handleChange}
                      fullwidth={true}
                      placeholder="Enter amount in $"
                    />
                    <div className="mt-3 flex items-center gap-2 p-3 bg-blue-50 rounded-xl">
                      <Info className="w-4 h-4 text-blue-600" />
                      <p className="text-sm text-blue-700">
                        You have paid <span className="font-semibold">${paidAmount}</span> in total.
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="mt-4 flex items-center gap-2 p-3 bg-amber-50 rounded-xl">
                    <AlertCircle className="w-4 h-4 text-amber-600" />
                    <p className="text-sm text-amber-700">You have paid $0 in total.</p>
                  </div>
                )}
              </div>
            )}

            {/* Date Selection */}
            {dateBoxes !== "hidden" && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8 animate-fadeIn">
                <SectionHeader 
                  icon={Calendar} 
                  title="Select Dates" 
                  subtitle="Choose your preferred dates"
                  color="amber"
                />
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                  <DateBox
                    dependentOn={true}
                    key={"StartDate"}
                    id={"StartDate"}
                    label="Start Date"
                    error={errors?.startDate}
                    value={requestData?.startDate}
                    name={"StartDate"}
                    onChange={handleChange}
                    minDate={moment().add(0, "years").toDate()}
                  />
                  <DateBox
                    dependentOn={true}
                    key={"EndDate"}
                    id={"EndDate"}
                    label="End Date"
                    error={errors?.endDate}
                    value={requestData?.endDate}
                    name={"EndDate"}
                    onChange={handleChange}
                    minDate={moment().add(0, "years").toDate()}
                  />
                </div>
              </div>
            )}

            {/* Documents & Upload Section */}
            {showDocs && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8 animate-fadeIn">
                {/* Available Forms */}
                <SectionHeader 
                  icon={FileText} 
                  title="Available Request Forms" 
                  subtitle="Download the required forms"
                  color="blue"
                />
                
                <div className="mt-4 bg-gray-50 rounded-xl border border-gray-200 overflow-hidden">
                  {docs?.length > 0 ? (
                    <div className="divide-y divide-gray-200">
                      {docs.map((doc) => (
                        <div 
                          key={doc.documentID} 
                          className="p-4 hover:bg-gray-100 transition-colors duration-200"
                        >
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                          <div className="flex items-start sm:items-center gap-3 min-w-0">
                            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                              <FileText className="w-5 h-5 text-blue-600" />
                            </div>
                            <span className="text-sm font-medium text-gray-900 break-words min-w-0">{doc.docName}</span>
                          </div>
                          <a 
                            href={doc.downloadLink} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-blue-50 hover:bg-blue-100 text-blue-600 transition-colors duration-200 w-full sm:w-auto"
                          >
                            <Download className="w-5 h-5" />
                            <span className="text-sm sm:hidden">Download</span>
                          </a>
                        </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <EmptyState 
                      icon={FileText}
                      title="No Documents Available"
                      description="There are no forms required for this request type"
                    />
                  )}
                </div>

                {/* Upload Section */}
                {docs.length > 0 && !pendingRequestFound && (
                  <div className="mt-6">
                    <div className="flex items-center gap-2 mb-4">
                      <FileUp className="w-5 h-5 text-gray-700" />
                      <h4 className="font-semibold text-gray-900">Upload Documents</h4>
                    </div>
                    
                    <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-xl mb-4">
                      <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0" />
                      <p className="text-sm text-amber-700">Please upload files in PDF format only</p>
                    </div>
                    
                    <Uploader
                      label={isUploadedFileValidError ? "test" : null}
                      error={isUploadedFileValidError}
                      onUpload={(files) => setUploadDocs(files)}
                      files={uploadedDocs}
                    />

                    {/* Notes Section */}
                    <div className="mt-6">
                      <div className="flex items-center gap-2 mb-3">
                        <MessageSquare className="w-5 h-5 text-gray-700" />
                        <h4 className="font-semibold text-gray-900">
                          {dateBoxes !== "hidden" && notesBox === "hidden" && newPHidden === "hidden" && amountBox === "hidden"
                            ? "Notes"
                            : "Reason"}
                        </h4>
                      </div>
                      
                      <textarea
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 transition-all duration-200 text-gray-900 placeholder-gray-400 resize-none text-sm outline-none"
                        id="notes"
                        name="notes"
                        rows={4}
                        onChange={handleChange}
                        value={requestData?.notes || ""}
                        placeholder={`Write a ${dateBoxes !== "hidden" && notesBox === "hidden" && newPHidden === "hidden" && amountBox === "hidden" ? "note" : "reason"} for your request...`}
                      />
                      <p className="mt-2 text-xs text-gray-500">
                        Provide any additional information that might help process your request.
                      </p>
                    </div>

                    {/* Submit Button */}
                    {uploadedDocs.length > 0 && (
                      <div className="mt-6">
                        <button
                          disabled={loading}
                          onClick={submitClicked}
                          className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white font-semibold rounded-xl shadow-lg shadow-blue-500/25 hover:shadow-xl hover:shadow-blue-500/30 hover:scale-102 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                        >
                          {loading ? (
                            <>
                              <Loader2 className="w-5 h-5 animate-spin" />
                              Submitting...
                            </>
                          ) : (
                            <>
                              <Send className="w-5 h-5" />
                              Submit Request
                            </>
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Pending Request Warning */}
                {pendingRequestFound && (
                  <div className="mt-6 flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                    <Clock className="w-5 h-5 text-amber-600 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-amber-800">Pending Request Found</p>
                      <p className="text-sm text-amber-700">You already have a pending request of this type. Please wait for it to be processed.</p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Request History */}
            {tempHistory.length > 0 && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8 animate-fadeIn">
                <SectionHeader 
                  icon={History} 
                  title="Request History" 
                  subtitle="Track the status of your previous requests"
                  color="purple"
                />
                
                <div className="mt-4 overflow-hidden rounded-xl border border-gray-200">
                  {/* Desktop Table */}
                  <div className="hidden sm:block overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                            Request Type
                          </th>
                          <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                            Date
                          </th>
                          <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                            Status
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {tempHistory.map((item) => (
                          <tr key={item.studentRequestId} className="hover:bg-gray-50 transition-colors duration-150">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                                  <FileText className="w-4 h-4 text-blue-600" />
                                </div>
                                <span className="text-sm font-medium text-gray-900">{item.requestTypeName}</span>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="text-sm text-gray-600">
                                {new Date(item.requestDate)?.toLocaleDateString("en-gb", {
                                  day: "numeric",
                                  month: "short",
                                  year: "numeric"
                                })}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <StatusBadge status={item.requestStatus} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile Cards */}
                  <div className="sm:hidden divide-y divide-gray-200">
                    {tempHistory.map((item) => (
                      <div key={item.studentRequestId} className="p-4 hover:bg-gray-50">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                              <FileText className="w-4 h-4 text-blue-600" />
                            </div>
                            <span className="text-sm font-medium text-gray-900">{item.requestTypeName}</span>
                          </div>
                          <StatusBadge status={item.requestStatus} />
                        </div>
                        <p className="text-xs text-gray-500 ml-10">
                          {new Date(item.requestDate)?.toLocaleDateString("en-gb", {
                            day: "numeric",
                            month: "short",
                            year: "numeric"
                          })}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MakeARequest;